import './workQueue.css';

import { NoteHistory, OwnerCell, WorkItemBadges, WorkItemForm } from './WorkItemForm';
import { ProviderDepartmentItem, ProviderWorkItems } from './useProviderWorkItems';
import React, { useState } from 'react';
import {
  WorkItemNote,
  WorkItemSubmission,
  WorkQueueLists,
  formatWhen,
  statusLabel,
  teamLabel,
  useWorkQueueLists
} from './workQueueLists';

import axios from 'axios';

const RELATION_LABELS: Record<ProviderDepartmentItem['relation'], { label: string; title: string }> = {
  blocked: {
    label: 'Blocking this provider',
    title: 'A Level 1 configuration rule blocks this provider in this department.'
  },
  deep: {
    label: 'Level 2 build finding',
    title: 'Passed every configuration rule but the deep probe found an Epic build problem here.'
  },
  other: {
    label: 'Other department',
    title: 'Not blocking this provider in the current snapshot, but the department has a work item.'
  }
};

/** One-line state for collapsed headers and the pre-check summary. */
export function summarizeWorkItems(data: ProviderWorkItems | null, lists: WorkQueueLists): string | null {
  if (!data) return null;
  const parts: string[] = [];
  const a = data.annotation;
  if (a) {
    parts.push(statusLabel(lists, a.status));
    const owner = a.assignedTo?.displayName ?? (a.assignedTeam ? teamLabel(lists, a.assignedTeam) : null);
    if (owner) parts.push(owner);
    parts.push(`${a.noteCount} note${a.noteCount === 1 ? '' : 's'}`);
  } else {
    parts.push('No provider item');
  }
  const openDepartments = data.departments.filter((department) => department.annotation && !department.annotation.addressed);
  if (openDepartments.length > 0) {
    parts.push(`${openDepartments.length} open department item${openDepartments.length === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

interface DepartmentRowProps {
  department: ProviderDepartmentItem;
  lists: WorkQueueLists;
  onSaved: () => void;
}

const DepartmentItemRow: React.FC<DepartmentRowProps> = ({ department, lists, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<WorkItemNote[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/epic-blocks/department-notes/${department.departmentId}`, { withCredentials: true });
      setNotes(response.data.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!open && notes === undefined) loadNotes();
    setOpen(!open);
  };

  const submit = async (submission: WorkItemSubmission): Promise<string | null> => {
    try {
      const response = await axios.post(`/api/epic-blocks/department-notes/${department.departmentId}`, submission, {
        withCredentials: true
      });
      setNotes((current) => [response.data.note, ...(current || [])]);
      onSaved();
      return null;
    } catch (err: any) {
      return err?.response?.data?.error || 'Failed to add the department note.';
    }
  };

  const relation = RELATION_LABELS[department.relation];
  return (
    <li className="wq-profile-dept">
      <div className="wq-profile-dept-row">
        <div className="wq-profile-dept-name">
          <strong>{department.departmentName || 'Unknown department'}</strong>
          <small> {department.departmentId}</small>
          <div className={`wq-sub wq-relation-${department.relation}`} title={relation.title}>{relation.label}</div>
        </div>
        <WorkItemBadges annotation={department.annotation} lists={lists} />
        <OwnerCell annotation={department.annotation} lists={lists} />
        <button type="button" className="btn btn-sm btn-secondary" onClick={toggle}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open && (
        <div className="wq-panel wq-profile-dept-panel">
          <WorkItemForm
            key={department.departmentId}
            annotation={department.annotation}
            lists={lists}
            placeholder="Department-level note: ticket number, what was requested..."
            onSubmit={submit}
          />
          <NoteHistory
            notes={notes}
            loading={loading}
            lists={lists}
            emptyText="No notes on this department yet."
            context={(note) =>
              note.providersBehindFix !== null && note.providersBehindFix !== undefined
                ? `(${note.providersBehindFix} behind the fix)`
                : null
            }
          />
        </div>
      )}
    </li>
  );
};

interface ProviderWorkQueuePanelProps {
  npi: string;
  data: ProviderWorkItems | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  open: boolean;
  onToggle: () => void;
}

/**
 * The provider's work-queue item and its departments' items on the profile page,
 * editable here as well as from the queues. Notes go through the same endpoints,
 * so whichever page an analyst works from, both stay in step.
 */
const ProviderWorkQueuePanel: React.FC<ProviderWorkQueuePanelProps> = ({ npi, data, loading, error, reload, open, onToggle }) => {
  const lists = useWorkQueueLists();

  const submitProviderNote = async (submission: WorkItemSubmission): Promise<string | null> => {
    try {
      await axios.post(`/api/epic-blocks/notes/${npi}`, submission, { withCredentials: true });
      await reload();
      return null;
    } catch (err: any) {
      return err?.response?.data?.error || 'Failed to save the note.';
    }
  };

  const summary = summarizeWorkItems(data, lists);
  const coveredBy = data?.coveredByDepartmentItem
    ? data.departments.find((department) => department.departmentId === data.coveredByDepartmentItem!.departmentId)
    : undefined;

  return (
    <div className="wq-profile-card" id="work-queue-panel">
      <button type="button" className="wq-profile-header" aria-expanded={open} onClick={onToggle}>
        <span className="wq-profile-title">Work queue</span>
        <span className="wq-profile-summary">
          {loading && !data ? 'Loading…' : error ? error : summary}
        </span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && data && (
        <div className="wq-profile-body">
          {data.annotation?.systemProposedResolution && (
            <div className="exclusion-note">
              The {data.snapshotDate} snapshot shows no block for this provider, but the item is still open. The next
              rescan closes it as Auto-Resolved; set Resolved now if you have confirmed the fix.
            </div>
          )}
          {data.coveredByDepartmentItem && (
            <div className="exclusion-note">
              An open department item ({statusLabel(lists, data.coveredByDepartmentItem.status)}) already covers{' '}
              <strong>{coveredBy?.departmentName || data.coveredByDepartmentItem.departmentId}</strong>, which is
              blocking this provider. Work it once there rather than here.
            </div>
          )}

          <h4 className="wq-profile-heading">
            This provider
            {data.priority && (
              <span className={`priority-pill priority-${data.priority.toLowerCase()}`} style={{ marginLeft: '0.5rem' }}>
                {data.priority}
              </span>
            )}
            {data.effectiveStatus && <span className="wq-sub" style={{ marginLeft: '0.5rem' }}>{data.effectiveStatus}</span>}
          </h4>
          {data.annotation && (
            <div className="wq-profile-state">
              <WorkItemBadges annotation={data.annotation} lists={lists} />
              <OwnerCell annotation={data.annotation} lists={lists} />
              <span className="wq-sub">last note {formatWhen(data.annotation.latestNoteAt)}</span>
            </div>
          )}
          <div className="wq-panel">
            <WorkItemForm
              key={npi}
              annotation={data.annotation}
              lists={lists}
              placeholder="What you found, ticket number, next step..."
              onSubmit={submitProviderNote}
            />
            <NoteHistory
              notes={data.notes}
              loading={false}
              lists={lists}
              emptyText="No notes on this provider yet."
              context={(note) => (note.providerStatus ? `(${note.providerStatus})` : null)}
            />
          </div>

          <h4 className="wq-profile-heading">
            Department items
            <span className="wq-sub" style={{ marginLeft: '0.5rem' }}>
              departments blocking this provider, plus any of its departments with a work item
            </span>
          </h4>
          {data.departments.length === 0 ? (
            <p className="wq-history-empty">No departments are blocking this provider in the current snapshot.</p>
          ) : (
            <ul className="wq-profile-depts">
              {data.departments.map((department) => (
                <DepartmentItemRow key={department.departmentId} department={department} lists={lists} onSaved={reload} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ProviderWorkQueuePanel;
